/**
 * E002-T040: WebhookService
 *
 * Service for calling workflow engine webhooks with batching and retry support.
 *
 * Features:
 * - Batch processing (configurable batch size)
 * - Rate limiting (interval between batches)
 * - Retry with exponential backoff
 * - Abort support
 */

import { Service } from '@toeverything/infra';

// =============================================================================
// Types
// =============================================================================

export interface BatchConfig {
  /** Number of rows per batch (1, 5, 10, or Infinity for all) */
  batchSize: number;
  /** Delay between batches in milliseconds */
  intervalMs: number;
  /** What to do on failure */
  onFailure: 'continue' | 'stop';
  /** Retry configuration */
  retry: {
    enabled: boolean;
    maxAttempts: number;
    initialDelayMs: number;
    maxDelayMs: number;
    backoffMultiplier: number;
  };
}

export interface WebhookCallResult {
  /** Whether the call succeeded */
  success: boolean;
  /** Response data from webhook */
  data?: Record<string, unknown>;
  /** Error message if failed */
  error?: string;
  /** HTTP status code */
  statusCode?: number;
  /** Duration in milliseconds */
  duration: number;
  /** Number of retry attempts */
  retryAttempts: number;
}

export interface BatchResult {
  /** Batch index (0-based) */
  batchIndex: number;
  /** Results for each row in the batch */
  results: WebhookCallResult[];
  /** Total duration for this batch */
  duration: number;
  /** Number of successful calls */
  succeeded: number;
  /** Number of failed calls */
  failed: number;
}

/** Default batch configuration */
export const DEFAULT_BATCH_CONFIG: BatchConfig = {
  batchSize: 1, // Process one row at a time by default
  intervalMs: 100, // 100ms between batches
  onFailure: 'continue', // Continue on failure by default
  retry: {
    enabled: true,
    maxAttempts: 3,
    initialDelayMs: 1000,
    maxDelayMs: 30000,
    backoffMultiplier: 2,
  },
};

// =============================================================================
// Service
// =============================================================================

export class WebhookService extends Service {
  /**
   * Call a webhook with a single payload
   */
  async callWebhook(
    webhookUrl: string,
    payload: Record<string, unknown>,
    signal?: AbortSignal,
    retryConfig?: BatchConfig['retry']
  ): Promise<WebhookCallResult> {
    const config = retryConfig ?? DEFAULT_BATCH_CONFIG.retry;
    const startTime = Date.now();
    let lastError: string | undefined;
    let attempts = 0;

    while (attempts <= (config.enabled ? config.maxAttempts : 0)) {
      if (signal?.aborted) {
        return {
          success: false,
          error: 'Aborted',
          duration: Date.now() - startTime,
          retryAttempts: attempts,
        };
      }

      try {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal,
        });

        const duration = Date.now() - startTime;

        if (response.ok) {
          let data: Record<string, unknown> | undefined;
          try {
            data = await response.json();
          } catch {
            // Response might not be JSON
            data = { rawResponse: await response.text() };
          }

          return {
            success: true,
            data,
            statusCode: response.status,
            duration,
            retryAttempts: attempts,
          };
        }

        // Non-OK response
        lastError = `HTTP ${response.status}: ${response.statusText}`;

        // Don't retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          return {
            success: false,
            error: lastError,
            statusCode: response.status,
            duration,
            retryAttempts: attempts,
          };
        }
      } catch (error) {
        lastError = error instanceof Error ? error.message : 'Unknown error';

        // Don't retry on abort
        if (error instanceof DOMException && error.name === 'AbortError') {
          return {
            success: false,
            error: 'Aborted',
            duration: Date.now() - startTime,
            retryAttempts: attempts,
          };
        }
      }

      attempts++;

      // Wait before retry with exponential backoff
      if (attempts <= config.maxAttempts && config.enabled) {
        const delay = Math.min(
          config.initialDelayMs *
            Math.pow(config.backoffMultiplier, attempts - 1),
          config.maxDelayMs
        );
        await this.sleep(delay, signal);
      }
    }

    return {
      success: false,
      error: lastError ?? 'Max retries exceeded',
      duration: Date.now() - startTime,
      retryAttempts: attempts,
    };
  }

  /**
   * Process payloads in batches
   */
  async processBatches(
    webhookUrl: string,
    payloads: Array<Record<string, unknown>>,
    config: BatchConfig,
    signal: AbortSignal,
    onBatchComplete?: (result: BatchResult) => void,
    onRowComplete?: (rowIndex: number, result: WebhookCallResult) => void
  ): Promise<{
    results: WebhookCallResult[];
    totalDuration: number;
    succeeded: number;
    failed: number;
    stopped: boolean;
  }> {
    const results: WebhookCallResult[] = [];
    const startTime = Date.now();
    let succeeded = 0;
    let failed = 0;
    let stopped = false;

    // Split into batches
    const batches = this.createBatches(payloads, config.batchSize);

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      if (signal.aborted) {
        stopped = true;
        break;
      }

      const batch = batches[batchIndex];
      const batchStartTime = Date.now();
      const batchResults: WebhookCallResult[] = [];
      let batchSucceeded = 0;
      let batchFailed = 0;

      // Process each row in the batch
      for (let i = 0; i < batch.length; i++) {
        if (signal.aborted) {
          stopped = true;
          break;
        }

        const payload = batch[i];
        const rowIndex = batchIndex * config.batchSize + i;

        const result = await this.callWebhook(
          webhookUrl,
          payload,
          signal,
          config.retry
        );

        results.push(result);
        batchResults.push(result);

        if (result.success) {
          succeeded++;
          batchSucceeded++;
        } else {
          failed++;
          batchFailed++;

          // Handle failure based on config
          if (config.onFailure === 'stop') {
            stopped = true;
            onRowComplete?.(rowIndex, result);
            break;
          }
          // 'continue' just continues
        }

        onRowComplete?.(rowIndex, result);
      }

      // Report batch completion
      onBatchComplete?.({
        batchIndex,
        results: batchResults,
        duration: Date.now() - batchStartTime,
        succeeded: batchSucceeded,
        failed: batchFailed,
      });

      if (stopped) break;

      // Wait between batches (if not the last batch)
      if (batchIndex < batches.length - 1 && config.intervalMs > 0) {
        await this.sleep(config.intervalMs, signal);
      }
    }

    return {
      results,
      totalDuration: Date.now() - startTime,
      succeeded,
      failed,
      stopped,
    };
  }

  /**
   * Split payloads into batches
   */
  private createBatches<T>(items: T[], batchSize: number): T[][] {
    if (batchSize === Infinity || batchSize >= items.length) {
      return [items];
    }

    const batches: T[][] = [];
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize));
    }
    return batches;
  }

  /**
   * Sleep with abort support
   */
  private sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const timeout = setTimeout(resolve, ms);

      signal?.addEventListener('abort', () => {
        clearTimeout(timeout);
        reject(new DOMException('Aborted', 'AbortError'));
      });
    });
  }
}
